import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '@/components/PageHeader';
import { useToast } from '@/components/Toast';
import { useMe } from '@/context/MeContext';
import { useSupportMode } from '@/context/SupportModeContext';
import { api } from '@/api/client';
import { formatApiError } from '@/utils/formatApiError';

export default function SupportModePage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { me } = useMe();
  const { supportMode, activateSupportMode } = useSupportMode();
  const [organizationId, setOrganizationId] = useState(me?.organizationId ?? '');
  const [targetUserId, setTargetUserId] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const canOpen = me?.permissions.canOpenSupportView ?? false;

  async function openView(e: React.FormEvent) {
    e.preventDefault();
    if (!reason.trim()) {
      setError('A reason is required for support view');
      return;
    }
    if (!organizationId.trim() && !targetUserId.trim()) {
      setError('Organization ID or target user ID is required');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const result = await api.support.openView({
        organizationId: organizationId.trim() || undefined,
        targetUserId: targetUserId.trim() || undefined,
        reason: reason.trim(),
      });
      activateSupportMode({
        organizationId: result.organizationId,
        organizationName: result.organizationName,
        targetUserId: result.targetUserId,
        actorEmail: me?.email ?? null,
        actorRole: me?.role ?? null,
        message: result.message,
      });
      toast('Support view opened', 'success');
      navigate('/', { replace: true });
    } catch (err) {
      setError(formatApiError(err, 'Could not open support view'));
    } finally {
      setSubmitting(false);
    }
  }

  if (!canOpen) {
    return (
      <div>
        <PageHeader title="Support view" subtitle="Customer data access" />
        <div className="alert alert-error">Support view is not available for your role.</div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Support view"
        subtitle="Open an audited read-only view of a customer organization"
      />
      {supportMode.active ? (
        <div className="alert alert-info" role="status">
          Support mode is active for{' '}
          <strong>{supportMode.organizationName || supportMode.organizationId || 'organization'}</strong>.
          Use the banner to exit before opening another view.
        </div>
      ) : null}
      <form className="card section support-mode-form" onSubmit={(e) => void openView(e)}>
        <p className="muted">
          All support views are audit-logged. Destructive actions remain disabled while support mode is active.
        </p>
        {error ? <div className="alert alert-error">{error}</div> : null}
        <div className="admin-form-grid">
          <label>
            Organization ID
            <input
              value={organizationId}
              onChange={(e) => setOrganizationId(e.target.value)}
              placeholder="UUID of customer organization"
            />
          </label>
          <label>
            Target user ID (optional)
            <input
              value={targetUserId}
              onChange={(e) => setTargetUserId(e.target.value)}
              placeholder="UUID to narrow view to one user"
            />
          </label>
          <label className="admin-form-grid__full">
            Reason
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              required
              minLength={3}
              placeholder="Ticket link or short justification"
            />
          </label>
        </div>
        <div className="account-form__actions">
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Opening…' : 'Open support view'}
          </button>
        </div>
      </form>
    </div>
  );
}
